import { SiteLayout } from "@/components/layout/SiteLayout";
import { RelatedPages } from "@/components/RelatedPages";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { NetworkRouter } from "@/components/NetworkRouter";
import { motion, type Variants } from "framer-motion";
import { breadcrumbSchema, personSchema, webPageSchema } from "@/lib/seo";
import { withNetworkUtm } from "@/lib/network";

const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

export default function WhyShrink() {
  const SEO_TITLE = "Why I use the word 'shrink' | Shariq Refai, MD, MBA";
  const SEO_DESCRIPTION =
    "Why I chose 'shrink' as the brand for the network. Familiar, accessible, and intentionally chosen to lower the intimidation barrier.";

  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Why I use the word 'shrink'", path: "/why-shrink" },
  ];

  return (
    <SiteLayout>
      <SEO
        title={SEO_TITLE}
        description={SEO_DESCRIPTION}
        url="/why-shrink"
        image="/og/why-shrink.jpg"
        keywords="why the word shrink, shrink meaning psychiatrist, The Shrink Network brand, shrinkMD, accessible mental health"
        schema={[
          personSchema,
          webPageSchema({
            path: "/why-shrink",
            title: SEO_TITLE,
            description: SEO_DESCRIPTION,
            breadcrumbs,
          }),
          breadcrumbSchema(breadcrumbs),
        ]}
      />

      {/* Intro */}
      <section className="pt-24 pb-12 md:pt-32 md:pb-16 px-4 md:px-8 bg-muted/30 border-b border-border/50">
        <div className="container mx-auto max-w-3xl">
          <Breadcrumbs
            items={[
              { name: "Home", href: "/" },
              { name: "Why I use the word \u201Cshrink\u201D" },
            ]}
          />
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="flex flex-col gap-5"
          >
            <span className="text-accent text-xs font-bold tracking-[0.25em] uppercase">
              A Founder Note
            </span>
            <h1 className="text-4xl md:text-5xl font-serif text-primary leading-tight tracking-tight">
              Why I use the word &ldquo;shrink.&rdquo;
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Body */}
      <article className="py-16 md:py-20 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-3xl prose prose-lg max-w-none prose-headings:font-serif prose-headings:font-normal prose-headings:text-primary prose-p:font-light prose-p:text-secondary prose-p:leading-relaxed prose-strong:text-primary prose-strong:font-medium prose-a:text-accent hover:prose-a:text-primary">
          <p>People sometimes ask whether "shrink" feels dismissive for a brand built by a psychiatrist. The short answer is: I chose it on purpose.</p>
          <p>"Shrink" is what most people actually say when they're talking about seeing a psychiatrist. It's the word in the lived language. "Psychiatrist" is the precise clinical word and it's also the word that keeps a lot of people from picking up the phone. "Therapist" doesn't map onto medication management. The wellness vocabulary is welcoming but vague.</p>
          <p>"Shrink" sits in between. It's familiar without being dismissive of what the work actually is. It's the word a friend uses to a friend. It lowers the activation energy on a conversation that already feels heavy.</p>
          <p>I'm not trying to trivialize psychiatric care by using it. The clinical work is serious and the writing on every site in the network treats it that way. The goal of the brand name isn't to make the work feel smaller. It's to make access to understanding feel possible.</p>
          <p>The other reason: it works as a connective brand. Shrinkopedia, shrinkMD, shrinQ, shrinkiatry, Shrinktionary. Family resemblance across the network without any single site sounding cute or out of place. Every property gets to keep its own job and its own voice while sharing a quiet visual and verbal signature.</p>
          <p>That's the whole reason. No clever marketing story behind it. The word was already there. I just took it seriously enough to use it on purpose.</p>
        </div>
      </article>

      {/* Continue */}
      <section className="py-16 md:py-20 px-4 md:px-8 bg-muted/30 border-t border-border/50">
        <div className="container mx-auto max-w-3xl">
          <NetworkRouter
            eyebrow="Continue"
            cards={[
              { label: "Read the founder narrative", href: "/founder-of-the-shrink-network" },
              { label: "See the network", href: withNetworkUtm("https://shrinknetwork.com/why/", "why_shrink_page") },
              { label: "Read what I'm building", href: "/what-im-building" },
              { label: "Read the books", href: "/books" },
            ]}
          />
        </div>
      </section>
      <RelatedPages
        entries={[
          { href: "/about", label: "About Shariq Refai, MD, MBA", why: "The clinician making the naming call." },
          { href: "/founder-of-the-shrink-network", label: "Founder of The Shrink Network", why: "Why the network exists and how the sites fit together." },
          { href: "/projects", label: "Projects across the network", why: "The ventures the shrink name is attached to." },
        ]}
      />
    </SiteLayout>
  );
}
